import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { getRollensicht, setRollensicht } from '../lib/rollensicht';

/**
 * Umschalter für die Testsicht auf eine Rolle.
 *
 * Sichtbar nur für Konten, die Rollen verwalten dürfen – für alle anderen wäre
 * er ohne Nutzen und nur ein weiterer Knopf.
 *
 * Ein Wechsel lädt die Seite neu. Das ist grob, aber ehrlich: Die Rechte
 * stecken in jedem geladenen Datensatz und in jeder offenen Liste; sie
 * nachträglich zu beschneiden hieße, jede Komponente einzeln zu überzeugen.
 * Der Neuladeweg lässt keinen Rest der vorigen Sicht stehen.
 */
type Rolle = { id: number; key: string; name: string };

/** Rollenliste des Kontos, solange keine Testsicht läuft – in der Sicht selbst kennt der Server nur die gewählte Rolle. */
const LISTE_KEY = 'ff.rollensicht.liste';

function gespeicherteListe(): Rolle[] {
  try {
    return JSON.parse(localStorage.getItem(LISTE_KEY) ?? '[]');
  } catch {
    return [];
  }
}

export function RollensichtSwitch() {
  const { user } = useAuth();
  const aktiv = getRollensicht();
  const [rollen, setRollen] = useState<Rolle[]>(aktiv ? gespeicherteListe() : []);

  const darfTesten = user?.isSuperadmin || user?.permissions?.['admin.roles.read']?.effect === 'allow';

  // Nur außerhalb der Testsicht nachladen: In ihr schneidet der Server die
  // Liste auf die gewählte Rolle zusammen, und der Umschalter hätte keine
  // andere Wahl mehr anzubieten.
  useEffect(() => {
    if (!darfTesten || aktiv) return;
    api
      .get('/api/auth/me/role-views')
      .then((data) => {
        const liste: Rolle[] = data.roles ?? [];
        setRollen(liste);
        localStorage.setItem(LISTE_KEY, JSON.stringify(liste));
      })
      .catch(() => setRollen([]));
  }, [darfTesten, aktiv]);

  // In aktiver Sicht bleibt der Umschalter stehen, auch wenn das Recht
  // `admin.roles.read` gerade weggeschnitten ist – sonst gäbe es keinen Weg
  // zurück außer dem Browserspeicher.
  if (!darfTesten && !aktiv) return null;

  // Die aktive Rolle muss als Option vorhanden sein. Fehlt sie, passt der Wert
  // des Feldes zu keiner Option, es zeigt „Eigene Rechte“ an – und diese
  // Option anzuwählen löst keinen Wechsel aus, weil sie schon angezeigt ist.
  const optionen = [...rollen];
  if (aktiv && !optionen.some((rolle) => rolle.id === aktiv)) {
    optionen.push({ id: aktiv, key: '', name: user?.rollensicht?.name ?? 'Rolle' });
  }

  const wechseln = (wert: string) => {
    setRollensicht(wert ? Number(wert) : null);
    window.location.reload();
  };

  // Das Feld sitzt im Benutzermenü. Das Menü schließt bei jedem Klick in sich –
  // ohne `stopPropagation` verschwände es samt Feld, bevor die Auswahl
  // ankäme. Schrift und Größe sind die der übrigen Kopfzeilenfelder; die
  // Breite ist fest, damit der Rollenname das Menü nicht aufweitet.
  return (
    <>
      <div className="dropdown-divider" />
      <div style={{ padding: '6px 10px' }} onClick={(event) => event.stopPropagation()}>
        <label className="small subtle" htmlFor="rollensicht-feld" style={{ display: 'block', marginBottom: 4 }}>
          Testsicht auf eine Rolle
        </label>
        <select
          id="rollensicht-feld"
          value={aktiv ?? ''}
          onChange={(event) => wechseln(event.target.value)}
          title={aktiv ? 'Testsicht aktiv – hier zurück zu den eigenen Rechten' : 'Anwendung mit den Rechten einer Rolle ansehen'}
          style={{
            width: 224,
            fontSize: 13.5,
            fontWeight: 600,
            ...(aktiv ? { borderColor: 'var(--warning)', background: 'var(--warning-soft)', color: 'var(--warning-text)' } : {}),
          }}
        >
          <option value="">{user?.isSuperadmin ? 'Als Superadmin (eigene Rechte)' : 'Eigene Rechte'}</option>
          {optionen.map((rolle) => (
            <option key={rolle.id} value={rolle.id}>
              Als {rolle.name}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

/**
 * Dauerhaftes Band, solange die Testsicht aktiv ist.
 *
 * Nicht wegklickbar und über allem: Wer vergisst, dass er eine fremde Rolle
 * trägt, hält fehlende Rechte für einen Fehler der Anwendung – und sucht ihn
 * an der falschen Stelle.
 */
export function RollensichtBanner() {
  const { user } = useAuth();
  if (!user?.rollensicht) return null;

  return (
    // Fest am unteren Rand statt im Fluss: Die App-Hülle ist ein Gitter, ein
    // Band darin würde zur Spalte statt zum Balken. Unten und nicht oben,
    // damit es das Markenband und die Notfallmeldung nicht verdeckt.
    <div
      className="alert warning small"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        margin: 0,
        borderRadius: 0,
        justifyContent: 'center',
        gap: 12,
        boxShadow: '0 -2px 12px rgba(0,0,0,.25)',
      }}
    >
      <span>
        <strong>Testsicht aktiv:</strong> Sie sehen die Anwendung mit den Rechten der Rolle „
        {user.rollensicht.name}“. Änderungen werden im Audit-Log als Testsicht vermerkt.
      </span>
      <button
        className="btn sm"
        onClick={() => {
          setRollensicht(null);
          window.location.reload();
        }}
      >
        Beenden
      </button>
    </div>
  );
}
